import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Camera, Edit2, User } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import AutosuggestInput from '../components/AutosuggestInput';
import VerificationBanner from '../components/VerificationBanner';
import VerificationUpload from '../components/VerificationUpload';
import ProfileCompletionTracker from '../components/ProfileCompletionTracker';

const Profile = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, updateUserProfile } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showVerificationModal, setShowVerificationModal] = useState<'id' | 'address' | null>(null);
  const isNewUser = searchParams.get('newUser') === 'true';

  const [formData, setFormData] = useState({
    fullName: user?.fullName || '',
    phone: user?.phone || '',
    photo: user?.photo || '',
    occupation: user?.occupation || '',
    location: user?.location || '',
    languages: user?.languages?.join(', ') || '',
    aboutMe: user?.aboutMe || '',
    creditScore: user?.creditScore || '',
    monthlyIncome: user?.monthlyIncome || '',
    employmentStatus: user?.employmentStatus || '',
    employmentLength: user?.employmentLength || '',
    references: user?.references || '',
    emergencyContact: user?.emergencyContact || ''
  });

  useEffect(() => {
    if (isNewUser) {
      setIsEditing(true);
    }
  }, [isNewUser]);

  useEffect(() => {
    if (user) {
      setFormData({
        fullName: user.fullName || '',
        phone: user.phone || '',
        photo: user.photo || '',
        occupation: user.occupation || '',
        location: user.location || '',
        languages: user.languages?.join(', ') || '',
        aboutMe: user.aboutMe || '',
        creditScore: user.creditScore || '',
        monthlyIncome: user.monthlyIncome || '',
        employmentStatus: user.employmentStatus || '',
        employmentLength: user.employmentLength || '',
        references: user.references || '',
        emergencyContact: user.emergencyContact || ''
      });
    }
  }, [user]);

  const handlePhotoClick = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setFormData(prev => ({ ...prev, photo: reader.result as string }));
        };
        reader.readAsDataURL(file);
      }
    };
    input.click();
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await updateUserProfile({
        ...formData,
        languages: formData.languages.split(',').map(lang => lang.trim()).filter(Boolean)
      });
      setIsEditing(false);
      if (isNewUser) {
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Failed to update profile:', error);
    } finally {
      setIsSaving(false);
    }
  };

  if (!user) {
    navigate('/login');
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Profile Completion Tracker */}
        {(isNewUser || !user?.fullName) && (
          <ProfileCompletionTracker user={formData} />
        )}

        {/* Verification Banner */}
        <VerificationBanner
          verificationStatus={user.verifications}
          onStartVerification={(type) => setShowVerificationModal(type)}
        />

        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
          {/* Profile Header */}
          <div className="relative h-32 bg-gradient-to-r from-indigo-600 to-purple-600">
            <button
              onClick={() => setIsEditing(!isEditing)}
              className="absolute top-4 right-4 px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg backdrop-blur-sm transition-all flex items-center space-x-2 font-medium"
            >
              <Edit2 className="h-4 w-4" />
              <span>{isEditing ? 'Cancel' : 'Edit Profile'}</span>
            </button>

            {/* Profile Picture */}
            <div className="absolute -bottom-16 left-8">
              <div className="relative">
                <div 
                  className="w-32 h-32 rounded-2xl border-4 border-white bg-white shadow-lg overflow-hidden cursor-pointer hover:opacity-90 transition-all group"
                  onClick={handlePhotoClick}
                >
                  {formData.photo ? (
                    <img 
                      src={formData.photo} 
                      alt={formData.fullName} 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center">
                      <User className="h-8 w-8 text-indigo-400" />
                    </div>
                  )}
                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <Camera className="h-8 w-8 text-white" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Profile Content */}
          <div className="px-8 pt-20 pb-8">
            <div className="space-y-8">
              {/* Basic Info Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name
                  </label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={formData.fullName}
                      onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      placeholder="Enter your full name"
                      required
                    />
                  ) : (
                    <p className="text-gray-900">{formData.fullName || 'Not provided'}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number
                  </label>
                  {isEditing ? (
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      placeholder="Enter your phone number"
                    />
                  ) : (
                    <p className="text-gray-900">{formData.phone || 'Not provided'}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Location
                  </label>
                  {isEditing ? (
                    <AutosuggestInput
                      type="location"
                      value={formData.location}
                      onChange={(value) => setFormData({ ...formData, location: value })}
                      placeholder="Enter your city"
                    />
                  ) : (
                    <p className="text-gray-900">{formData.location || 'Not provided'}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Occupation
                  </label>
                  {isEditing ? (
                    <AutosuggestInput
                      type="occupation"
                      value={formData.occupation}
                      onChange={(value) => setFormData({ ...formData, occupation: value })}
                      placeholder="Enter your occupation"
                    />
                  ) : (
                    <p className="text-gray-900">{formData.occupation || 'Not provided'}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Languages
                  </label>
                  {isEditing ? (
                    <AutosuggestInput
                      type="language"
                      value={formData.languages}
                      onChange={(value) => setFormData({ ...formData, languages: value })}
                      placeholder="Enter languages (comma-separated)"
                    />
                  ) : (
                    <p className="text-gray-900">
                      {formData.languages || 'Not provided'}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Monthly Income
                  </label>
                  {isEditing ? (
                    <input
                      type="number"
                      value={formData.monthlyIncome}
                      onChange={(e) => setFormData({ ...formData, monthlyIncome: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      placeholder="Enter your monthly income"
                    />
                  ) : (
                    <p className="text-gray-900">
                      {formData.monthlyIncome ? `$${formData.monthlyIncome}` : 'Not provided'}
                    </p>
                  )}
                </div>
              </div>

              {/* About Me */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  About Me
                </label>
                {isEditing ? (
                  <textarea
                    value={formData.aboutMe}
                    onChange={(e) => setFormData({ ...formData, aboutMe: e.target.value })}
                    rows={4}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    placeholder="Tell others about yourself..."
                  />
                ) : (
                  <p className="text-gray-900 whitespace-pre-wrap">
                    {formData.aboutMe || 'No bio provided'}
                  </p>
                )}
              </div>

              {/* Save Button */}
              {isEditing && (
                <div className="flex justify-end">
                  <button
                    onClick={handleSave}
                    disabled={isSaving}
                    className="px-6 py-2 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSaving ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Verification Upload Modal */}
      {showVerificationModal && (
        <VerificationUpload
          type={showVerificationModal}
          onClose={() => setShowVerificationModal(null)}
        />
      )}
    </div>
  );
};

export default Profile;